import { z } from 'zod';

export const notificationSeverities = ['success', 'error'] as const;
export const notificationSeveritySchema = z.enum(notificationSeverities);
export type NotificationSeverity = z.infer<typeof notificationSeveritySchema>;

export const notificationDefinitions = {
  'report.ready': { severity: 'success', actionUrl: '/reports' },
  'report.failed': { severity: 'error', actionUrl: '/reports' },
  'submission.ready': { severity: 'success', actionUrl: '/submissions' },
  'submission.failed': { severity: 'error', actionUrl: '/submissions' },
  'remittance.completed': {
    severity: 'success',
    actionUrl: '/remittances',
  },
  'remittance.failed': { severity: 'error', actionUrl: '/remittances' },
  'claimQuery.completed': { severity: 'success', actionUrl: '/queries' },
  'intakeItem.assigned': { severity: 'success', actionUrl: '/uploads' },
  'oru.orphaned': { severity: 'error', actionUrl: '/notifications' },
} as const satisfies Record<
  string,
  { severity: NotificationSeverity; actionUrl: `/${string}` }
>;

export type NotificationType = keyof typeof notificationDefinitions;

export const notificationTypes = Object.keys(notificationDefinitions) as [
  NotificationType,
  ...NotificationType[]
];
export const notificationTypeSchema = z.enum(notificationTypes);

export const reportReadyNotificationDataSchema = z.object({
  reportID: z.string().min(1),
  reportType: z.string().min(1),
});
export const reportFailedNotificationDataSchema = z.object({
  reason: z.string().min(1),
});
export const submissionReadyNotificationDataSchema = z.object({
  submissionID: z.string().min(1),
});
export const submissionFailedNotificationDataSchema = z.object({
  submissionID: z.string().min(1),
  reason: z.string().min(1),
  errors: z.array(z.string().min(1)).optional(),
});
export const remittanceCompletedNotificationDataSchema = z.object({
  siteID: z.string().min(1),
});
export const remittanceFailedNotificationDataSchema = z.object({
  siteID: z.string().min(1),
  reason: z.string().min(1),
});
export const claimQueryCompletedNotificationDataSchema = z.object({
  claimQueryID: z.string().min(1),
  providerName: z.string().min(1).optional(),
});
export const intakeItemAssignedNotificationDataSchema = z.object({
  intakeItemID: z.guid(),
  intakeNumber: z.number().int().positive(),
});

export const orphanedORUNotificationDataSchema = z.union([
  z.object({ count: z.number().int().positive() }),
  z.object({ messageIDs: z.array(z.string().min(1)).min(1) }),
]);

const notificationRecordBaseSchema = z.object({
  id: z.guid(),
  recipientCognitoID: z.string().min(1),
  severity: notificationSeveritySchema,
  actionUrl: z.string().startsWith('/'),
  createdAt: z.string(),
  readAt: z.string().nullable(),
});

export const notificationRecordSchema = z.discriminatedUnion('type', [
  notificationRecordBaseSchema.extend({
    type: z.literal('oru.orphaned'),
    data: orphanedORUNotificationDataSchema,
  }),
  notificationRecordBaseSchema.extend({
    type: z.literal('report.ready'),
    data: reportReadyNotificationDataSchema,
  }),
  notificationRecordBaseSchema.extend({
    type: z.literal('report.failed'),
    data: reportFailedNotificationDataSchema,
  }),
  notificationRecordBaseSchema.extend({
    type: z.literal('submission.ready'),
    data: submissionReadyNotificationDataSchema,
  }),
  notificationRecordBaseSchema.extend({
    type: z.literal('submission.failed'),
    data: submissionFailedNotificationDataSchema,
  }),
  notificationRecordBaseSchema.extend({
    type: z.literal('remittance.completed'),
    data: remittanceCompletedNotificationDataSchema,
  }),
  notificationRecordBaseSchema.extend({
    type: z.literal('remittance.failed'),
    data: remittanceFailedNotificationDataSchema,
  }),
  notificationRecordBaseSchema.extend({
    type: z.literal('claimQuery.completed'),
    data: claimQueryCompletedNotificationDataSchema,
  }),
  notificationRecordBaseSchema.extend({
    type: z.literal('intakeItem.assigned'),
    data: intakeItemAssignedNotificationDataSchema,
  }),
]);

export const notificationListResponseSchema = z.object({
  data: z.array(notificationRecordSchema),
  total: z.number().int().nonnegative(),
  unreadCount: z.number().int().nonnegative(),
  page: z.number().int().nonnegative(),
  pageSize: z.number().int().positive(),
});

export const setNotificationReadSchema = z.object({
  notificationID: z.guid(),
  read: z.boolean(),
});

export const markAllNotificationsReadSchema = z.object({
  all: z.literal(true),
});

export const notificationReadMutationSchema = z.union([
  setNotificationReadSchema,
  markAllNotificationsReadSchema,
]);

export const notificationWebSocketEnvelopeSchema = z.object({
  action: z.literal('notification.created'),
  notification: notificationRecordSchema,
});

const dispatchBaseSchema = z.object({
  action: z.literal('notification.dispatch'),
  recipientCognitoID: z.string().min(1),
  dedupeKey: z.string().min(1),
});

export const notificationDispatchSchema = z.discriminatedUnion('type', [
  dispatchBaseSchema.extend({
    type: z.literal('report.ready'),
    data: reportReadyNotificationDataSchema,
  }),
  dispatchBaseSchema.extend({
    type: z.literal('report.failed'),
    data: reportFailedNotificationDataSchema,
  }),
  dispatchBaseSchema.extend({
    type: z.literal('submission.ready'),
    data: submissionReadyNotificationDataSchema,
  }),
  dispatchBaseSchema.extend({
    type: z.literal('submission.failed'),
    data: submissionFailedNotificationDataSchema,
  }),
  dispatchBaseSchema.extend({
    type: z.literal('remittance.completed'),
    data: remittanceCompletedNotificationDataSchema,
  }),
  dispatchBaseSchema.extend({
    type: z.literal('remittance.failed'),
    data: remittanceFailedNotificationDataSchema,
  }),
  dispatchBaseSchema.extend({
    type: z.literal('claimQuery.completed'),
    data: claimQueryCompletedNotificationDataSchema,
  }),
  dispatchBaseSchema.extend({
    type: z.literal('intakeItem.assigned'),
    data: intakeItemAssignedNotificationDataSchema,
  }),
]);

const remittanceDispatchBaseSchema = z.object({
  action: z.literal('notification.dispatch.remittance'),
  dedupeKey: z.string().min(1),
  recipientCognitoID: z.string().min(1).optional(),
});

export const notificationRemittanceDispatchSchema = z.discriminatedUnion(
  'type',
  [
    remittanceDispatchBaseSchema.extend({
      type: z.literal('remittance.completed'),
      data: remittanceCompletedNotificationDataSchema,
    }),
    remittanceDispatchBaseSchema.extend({
      type: z.literal('remittance.failed'),
      data: remittanceFailedNotificationDataSchema,
    }),
  ]
);

export const notificationOrphanedORUDispatchSchema = z.object({
  action: z.literal('notification.dispatch.orphanedORU'),
  type: z.literal('oru.orphaned'),
  organizationID: z.guid(),
  dedupeKey: z.string().min(1),
  data: z.object({ count: z.number().int().positive() }),
  risAccessions: z.array(z.string().min(1)),
});

export type NotificationOrphanedORUDispatch = z.infer<
  typeof notificationOrphanedORUDispatchSchema
>;

export const notificationCleanupSchema = z.object({
  action: z.literal('notification.cleanup'),
});

export type NotificationRecord = z.infer<typeof notificationRecordSchema>;
export type NotificationListResponse = z.infer<
  typeof notificationListResponseSchema
>;
export type NotificationDispatch = z.infer<typeof notificationDispatchSchema>;
export type NotificationRemittanceDispatch = z.infer<
  typeof notificationRemittanceDispatchSchema
>;
export type NotificationWebSocketEnvelope = z.infer<
  typeof notificationWebSocketEnvelopeSchema
>;
